import React, { createContext, useState, useEffect, useContext, useRef } from 'react';

const SESSION_STORAGE_KEY = 'auth_session';
const SESSION_DURATION_MS = 55 * 60 * 1000; // 55 minutes

function isSessionValid(sessionObj) {
  if (!sessionObj) {
    return false;
  }

  const now = Date.now();

  // Check if expiry has passed
  if (sessionObj.expiresAt && now >= sessionObj.expiresAt) {
    console.debug('Session expired: expiry reached');
    return false;
  }

  // Validate required fields
  if (!sessionObj.token || !sessionObj.user) {
    console.debug('Session invalid: missing required fields');
    return false;
  }

  return true;
}

function clearSessionStorage() {
  sessionStorage.removeItem(SESSION_STORAGE_KEY);
  console.debug('Session cleared from storage');
}

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const refreshTimeoutRef = useRef(null);

  const clearSession = () => {
    clearSessionStorage();
    setIsAuthenticated(false);
    setUser(null);
  };


  const login = (serverToken, profile) => {
    // Create structured session object with server token
    const sessionObj = {
      token: serverToken,
      expiresAt: Date.now() + SESSION_DURATION_MS,
      user: profile,
    };

    // Store in sessionStorage
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionObj));
    console.debug('Session created', {
      email: profile.email,
      expiresAt: new Date(sessionObj.expiresAt),
    });

    // Update React state
    setUser(profile);
    setIsAuthenticated(true);
  };


  const logout = () => {
    clearSession();
  };

  const restoreSession = () => {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!stored) {
      return false;
    }

    try {
      const sessionObj = JSON.parse(stored);
      if (!isSessionValid(sessionObj)) {
        console.debug('Stored session is invalid, clearing');
        clearSessionStorage();
        return false;
      }

      console.debug('Session restored from storage');
      setUser(sessionObj.user);
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      console.error('Failed to restore session:', error);
      clearSessionStorage();
      return false;
    }
  };

  const isSessionValidCheck = () => {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!stored) {
      return false;
    }

    try {
      const sessionObj = JSON.parse(stored);
      return isSessionValid(sessionObj);
    } catch (error) {
      console.error('Failed to validate session:', error);
      return false;
    }
  };

  const requestReauth = (redirectPath = null) => {
    console.debug('Reauthentication requested');
    clearSession();
    return redirectPath || '/login';
  };

  // Refresh session by validating with server
  const refreshSession = async (retryCount = 0) => {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!stored) {
      console.debug('No session to refresh');
      return false;
    }

    try {
      const sessionObj = JSON.parse(stored);
      
      // Validate token with server
      const response = await fetch('/api/auth/session', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionObj.token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (retryCount === 0) {
          console.debug('Server validation failed, retrying once...');
          return refreshSession(1);
        }
        console.debug('Server validation failed, token may be revoked');
        return false;
      }

      // Server confirmed token is valid, extend client-side expiry
      sessionObj.expiresAt = Date.now() + SESSION_DURATION_MS;
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionObj));
      console.debug('Session refreshed and validated', {
        newExpiresAt: new Date(sessionObj.expiresAt),
      });
      return true;
    } catch (error) {
      if (retryCount === 0) {
        console.debug('Failed to refresh session, retrying once...', error);
        return refreshSession(1);
      }
      console.error('Failed to refresh session after retry:', error);
      return false;
    }
  };

  useEffect(() => {
    restoreSession();
    setIsLoading(false);
  }, []);

  // Set up session refresh timer - validates with server when about to expire
  useEffect(() => {
    if (!isAuthenticated) return;

    const scheduleRefresh = () => {
      const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (!stored) return;

      try {
        const sessionObj = JSON.parse(stored);
        const expiresAt = sessionObj.expiresAt;
        const now = Date.now();
        const timeUntilExpiry = expiresAt - now;

        const refreshThreshold = 10 * 60 * 1000; // 10 minutes in milliseconds

        if (timeUntilExpiry > refreshThreshold) {
          // Schedule refresh for when ~2 seconds remain
          const timeUntilRefresh = timeUntilExpiry - refreshThreshold;
          console.debug(`Session refresh scheduled in ${Math.round(timeUntilRefresh / 1000)}s`);
          
          refreshTimeoutRef.current = setTimeout(async () => {
            console.debug('Validating session with server before expiry');
            const refreshed = await refreshSession();
            if (!refreshed) {
              console.debug('Session validation failed, clearing session');
              clearSession();
            } else {
              // Reschedule for next refresh
              scheduleRefresh();
            }
          }, timeUntilRefresh);
        } else if (timeUntilExpiry > 0) {
          // Less than 2 seconds left, refresh immediately
          refreshTimeoutRef.current = setTimeout(async () => {
            const refreshed = await refreshSession();
            if (!refreshed) {
              clearSession();
            } else {
              scheduleRefresh();
            }
          }, 0);
        }
      } catch (error) {
        console.error('Failed to schedule refresh:', error);
      }
    };

    scheduleRefresh();

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [isAuthenticated]);

  let refreshTimeout = null;

  const contextValue = {
    // State
    isAuthenticated,
    isLoading,
    user,

    // Methods
    login,
    logout,
    clearSession,
    restoreSession,
    refreshSession,
    isSessionValid: isSessionValidCheck,
    requestReauth,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
